import React, { memo } from "react";
import clsx from "clsx";
import Text from "../../../../shared-components/css-modules/lexus/atoms/Text";
import styles from "./SpecsDetail.module.scss";
import type { SpecDataType } from "../../../../../../common-deprecated/types/SpecTypes";
import { useCarConfigSelector } from "../../../redux/store";
import { selectCarConfigSpecsSearchActiveResult } from "../../../redux/slices/CarConfigSpecsSearchSlice";
import type { SpecGroupType } from "../../../hooks/useSpecs";
import Popover from "../../../../shared-components/css-modules/lexus/vendor/popover/Popover";
import Button from "../../../../shared-components/css-modules/lexus/atoms/Button";
import Icon from "../../../../shared-components/css-modules/lexus/atoms/Icon";
import a11y from "../../../../shared-components/css-modules/lexus/utils/Accessibility.module.scss";
import { CarConfigLabel } from "../../../utils/labelUtils";

type Props = {
    group: SpecGroupType;
};

const SpecsDetail: React.FC<Props> = ({ group }) => {
    const activeResult = useCarConfigSelector(selectCarConfigSpecsSearchActiveResult);

    return (
        <div className={styles.wrapper}>
            <Text tag="p" variant="apheleia-body" spacing="spacing-3">
                {group.secondaryDescription}
            </Text>

            <img className={styles.image} src={group.image} alt={group.name} />

            <div className={styles.grid}>
                <div className={styles.list}>
                    <div className={styles.section}>
                        {group.categories.map((category) => (
                            <div key={category.id}>
                                {category.name && (
                                    <div className={styles.subheader}>
                                        <Text tag="h3" variant="apheleia-body-large" spacing="spacing-3">
                                            {category.name}
                                        </Text>
                                    </div>
                                )}

                                <dl role="list">
                                    {category.items.map((item) => {
                                        const isHighlighted = activeResult?.itemId === item.id;

                                        return (
                                            <div
                                                className={clsx(
                                                    styles["specs-list-item"],
                                                    isHighlighted && styles.highlight,
                                                )}
                                                key={item.id}
                                                ref={(ref) => {
                                                    if (isHighlighted && ref) {
                                                        setTimeout(() => {
                                                            ref.scrollIntoView({
                                                                behavior: "smooth",
                                                                block: "center",
                                                            });
                                                        }, 250);
                                                    }
                                                }}
                                            >
                                                <dt>
                                                    <Text tag="span" variant="apheleia-body">
                                                        {item.name}
                                                    </Text>
                                                    <div>
                                                        {item.description && (
                                                            <Popover placement="top">
                                                                <Popover.Trigger>
                                                                    <Button
                                                                        variant="clean"
                                                                        onClick={(event) => {
                                                                            event.stopPropagation();
                                                                        }}
                                                                    >
                                                                        <Icon
                                                                            variant="info"
                                                                            sizing="sm"
                                                                            aria-hidden="true"
                                                                        />
                                                                        <span className={a11y["sr-only"]}>
                                                                            <CarConfigLabel label="carConfigA11yButtonMoreInfoAbout" />
                                                                        </span>
                                                                    </Button>
                                                                </Popover.Trigger>
                                                                <Popover.Content>
                                                                    <div
                                                                        style={{ zIndex: "100" }}
                                                                        dangerouslySetInnerHTML={{
                                                                            __html: item.description,
                                                                        }}
                                                                    />
                                                                </Popover.Content>
                                                            </Popover>
                                                        )}
                                                    </div>
                                                </dt>
                                                <dd>
                                                    {group.type === "specs" && (
                                                        <Text variant="apheleia-body">
                                                            {(item as SpecDataType).value} {(item as SpecDataType).unit}
                                                        </Text>
                                                    )}
                                                </dd>
                                            </div>
                                        );
                                    })}
                                </dl>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default memo(SpecsDetail);
